import React from 'react';
import { useQuery } from 'react-query';
import { JobHttpService } from 'cms/features/feature_job/service';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Grid,
    Paper
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

function ReportsPage() {
    // Load user's company
    const { data: company, isLoading: companyLoading } = useQuery(
        'myCompany',
        () => JobHttpService.loadMyCompany()
    );

    // Load various data for reports
    const { data: jobsData } = useQuery(
        ['employerJobs', company?.id],
        () => {
            if (!company?.id) return { results: [], count: 0 };
            return JobHttpService.loadJobs({ filters: { company: company.id } });
        },
        {
            enabled: !!company?.id
        }
    );

    const { data: employeesData } = useQuery(
        ['employees', company?.id],
        () => {
            if (!company?.id) return { results: [], count: 0 };
            return JobHttpService.loadEmployees({ company: company.id });
        },
        {
            enabled: !!company?.id
        }
    );

    const { data: invoicesData } = useQuery(
        ['invoices', company?.id],
        () => {
            if (!company?.id) return { results: [], count: 0 };
            return JobHttpService.loadInvoices({ company: company.id });
        },
        {
            enabled: !!company?.id
        }
    );

    const { data: salarySpecsData } = useQuery(
        ['salarySpecifications', company?.id],
        () => {
            if (!company?.id) return { results: [], count: 0 };
            return JobHttpService.loadSalarySpecifications({ company: company.id });
        },
        {
            enabled: !!company?.id
        }
    );

    const jobs = jobsData?.results || [];
    const employees = employeesData?.results || employeesData || [];
    const invoices = invoicesData?.results || invoicesData || [];
    const salarySpecs = salarySpecsData?.results || salarySpecsData || [];

    // Calculate totals
    const totalInvoicesAmount = invoices.reduce((sum, inv) => {
        return sum + (parseFloat(inv.total_amount) || 0);
    }, 0);

    const paidInvoices = invoices.filter(inv => inv.status === 'paid').length;
    const pendingInvoices = invoices.filter(inv => inv.status === 'pending').length;

    if (companyLoading) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </section>
        );
    }

    if (!company) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        You need to create a company first. <Button href="/employer/my-company">Go to My Company</Button>
                    </Alert>
                </Container>
            </section>
        );
    }

    const statCards = [
        {
            label: 'Total Jobs',
            value: jobs.length,
            icon: <WorkIcon color="primary" />,
            color: 'primary'
        },
        {
            label: 'Total Employees',
            value: employees.length,
            icon: <PeopleIcon color="primary" />,
            color: 'primary'
        },
        {
            label: 'Total Invoices',
            value: invoices.length,
            icon: <ReceiptIcon color="primary" />,
            color: 'primary'
        },
        {
            label: 'Total Invoice Amount',
            value: `$${totalInvoicesAmount.toFixed(2)}`,
            icon: <AttachMoneyIcon color="primary" />,
            color: 'primary'
        },
        {
            label: 'Paid Invoices',
            value: paidInvoices,
            icon: <ReceiptIcon color="success" />,
            color: 'success'
        },
        {
            label: 'Pending Invoices',
            value: pendingInvoices,
            icon: <ReceiptIcon color="warning" />,
            color: 'warning'
        },
        {
            label: 'Salary Specifications',
            value: salarySpecs.length,
            icon: <AccountBalanceIcon color="primary" />,
            color: 'primary'
        }
    ];

    return (
        <section className="section-box mt-50">
            <Container maxWidth="lg">
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" fontWeight={600} gutterBottom>
                        Reports
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        View analytics and insights for your company
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {statCards.map((stat, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                {stat.label}
                                            </Typography>
                                            <Typography variant="h4" fontWeight={700} color={`${stat.color}.main`}>
                                                {stat.value}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ fontSize: 48, opacity: 0.3 }}>
                                            {stat.icon}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {statCards.length === 0 && (
                    <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
                        <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            No data available
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Reports will appear here as you add jobs, employees, and invoices.
                        </Typography>
                    </Paper>
                )}
            </Container>
        </section>
    );
}

export default ReportsPage;
